import { ProductKind, ProductType } from 'constants/product'
import { ProductSegment } from 'constants/product/productTypes'

import { AssignmentStage } from 'constants/assignment'
import { MonetaryValueDTO } from 'models/misc/monetaryValue'
import { Nullable } from 'models/helpers'
import { ProductCategory } from 'models/product'

/** Real estate property type */
export enum PropertyType {
  COMMERCIAL = 'COMMERCIAL',
  RESIDENTIAL = 'RESIDENTIAL'
}

export enum PropertyFloor {
  BASEMENT = 'BASEMENT',
  GROUND = 'GROUND',
  UPPER = 'UPPER',
  ATTIC = 'ATTIC',
}

/** Real estate property address */
export interface PropertyAddressDTO {
  /** Street name */
  street: string
  /** Building number */
  streetNumber: string
  /** Postal code */
  postalCode: string
  /** City */
  city: string
  /** Country */
  country: string
  /** Region code */
  regionCode: string
  /** GEO coordinates of the address */
  coordinates?: {
    lat: number
    lng: number
  }
}

export interface PropertyFloorInfo {
  floor?: PropertyFloor
  description: string
}

/** Surface area of a real estate property (platform `AreaDTO`). */
export interface PropertySurfaceDTO {
  /** Numeric surface value, expressed in `unit`. */
  value: number
  /** Unit of the surface value: square meters or square feet. */
  unit: 'SQM' | 'SQFT'
}

/** Real estate property DTO. */
export interface RealEstatePropertyPurchaseFlowDTO {
  /** Title of the real estate property. */
  title?: Nullable<string>
  /** Address of the real estate property. */
  address?: Nullable<PropertyAddressDTO>
  /** Type of property - COMMERCIAL or RESIDENTIAL. */
  propertyType?: Nullable<PropertyType>
}

export interface RealEstatePropertyDTO {
  /** Unique id of the real estate property */
  id: string
  /**
   * Where a creative is actually routed to, when that differs from the address.
   *
   * The two are NOT interchangeable and must not be collapsed:
   *   - `address.coordinates` is where the property IS — the precise geocode,
   *     used for map pins and for deriving the shoot timezone.
   *   - `creativeRoutingAddress.coordinates` is the closest place a creative can
   *     actually be routed to, used for travel distance and creative dispatch.
   *
   * A newly built or otherwise unmapped address has no routable point of its
   * own, so the front end pins the nearest one here (the BE create payload's own
   * words) rather than writing the snapped point over the address and losing
   * where the building is. Placement already reads
   * `creativeRoutingAddress ?: address`, so leaving this null keeps today's
   * behaviour.
   */
  creativeRoutingAddress?: Nullable<PropertyAddressDTO>
  /** Broker id */
  brokerId: string
  /** Title of the real estate property */
  title: string
  /** Description of the real estate property */
  externalInfo: string
  /** Address of the real estate property */
  address: PropertyAddressDTO
  /** Floor information of the real estate property */
  floorInfo: PropertyFloorInfo
  /** Real estate property reference id of the client */
  objectReferenceId: string
  /** Type of property - COMMERCIAL or RESIDENTIAL */
  propertyType: PropertyType
  /** Price of the real estate property */
  price: MonetaryValueDTO
  /** Surface area of the real estate property (optional; synced by the CRM integration). */
  surface?: PropertySurfaceDTO
  /** The product area band the property is priced in: its exact surface's band, else the band the client picked. */
  areaRange?: ProductSegment
  /** Workspace id */
  workspaceId: string
  /** List of order ids */
  orderIds: Array<string>
  /** Broker detail of the real estate property */
  broker: BrokerDetailsDTO
  /** Created date of the real estate property */
  createdOn: string // ISO-8061 String
}

export interface RealEstatePropertyAssignmentProductsDTO {
  id: number
  /** Product category (e.g. Real Estate, Event) */
  category: ProductCategory
  /** Product kind (e.g. ground photo) */
  kind: ProductKind
  /** Whether this product requires a creative (photographer) to be assigned — backend-computed (ISSUE-2538) */
  requiresCreative: boolean
  /** Whether this product is AI photo editing — backend-computed (ISSUE-2539), read instead of `type` */
  isAiPhotoEditing: boolean
  /** Whether this product is a virtual visit — backend-computed (ISSUE-2539), read instead of `type` */
  isVirtualVisit: boolean
  /** Whether this product is a floor plan — backend-computed (ISSUE-2539), read instead of `type` */
  isFloorPlan: boolean
  /** Whether this product is a video — backend-computed (ISSUE-2539), read instead of `type` */
  isVideo: boolean
  /** Whether this product is a virtual staging product — backend-computed (ISSUE-2539) */
  isStaging: boolean
  /** Whether this product is the 360° staging variant — backend-computed (ISSUE-2539), read instead of `type` */
  isStaging360: boolean
}

export interface RealEstatePropertyAssignmentDTO {
  /** Unique identifier of the real estate property */
  id: string
  /** Order id related to assignment */
  orderId: number
  /** Assignment stage */
  stage: AssignmentStage
  /** Whether the Assignment is cancelled */
  isCancelled: boolean
  /** Primary product of assignment */
  primaryProduct: RealEstatePropertyAssignmentProductsDTO
  /** List of products */
  products: RealEstatePropertyAssignmentProductsDTO[]
}

/** Paginated DTO containing a list of real estate property items. */
export interface PageRealEstatePropertyListItemDTO extends PageObject<RealEstatePropertyListItemDTO> { }

export interface RealEstatePropertyListItemDTO {
  /** Unique identifier of the real estate property */
  id: string
  /** Title of the real estate property */
  title?: string
  /** Address of the real estate property */
  address: PropertyAddressDTO
  /** Broker detail of the real estate property */
  broker: BrokerDetailsDTO
  /** Created date of the real estate property */
  createdOn: string // ISO-8061 String
  /** Updated date of the real estate property */
  updatedAt?: string // ISO-8061 String
  /** Real estate property reference id of the client integration */
  objectReferenceId?: string
  /** Real estate property usage */
  propertyUsage?: PropertyType
}

export interface SortObject {
  /** Is empty */
  empty: boolean
  /** Is sorted */
  sorted: boolean
  /** Is unsorted */
  unsorted: boolean
}

export interface PageableObject {
  /** Page offset */
  offset: number
  /** Page sort object */
  sort: SortObject,
  /** Page size */
  pageSize: number,
  /** Page number */
  pageNumber: number,
  /** Is paged */
  paged: boolean,
  /** Is unpaged */
  unpaged: boolean,
}

export interface PageObject<T> {
  /** Total number of pages */
  totalPages: number
  /** Total number of elements */
  totalElements: number
  /** Size of the page */
  size: number
  /** List of content T */
  content: T[]
  /** Current page number */
  number: number
  /** Sort object */
  sort: SortObject
  /** Is first page */
  first: boolean
  /** Is last page */
  last: boolean
  /** Number of elements */
  numberOfElements: number
  /** Pageable object */
  pageable: PageableObject
  /** Is empty */
  empty: boolean
  /** Created date of the real estate property ISO-8061 String */
  createdOn: string
}

export interface BrokerDetailsDTO {
  /** Broker name */
  name?: string
  /** Broker email */
  email?: string
  /** Broker phone */
  phone?: string
}

export interface BrokerDTO {
  /** Broker id */
  id: string
  /** Broker name */
  name: string
}

export interface PropertyWorkspaceInfoDTO {
  /** Property id */
  propertyId: string
  /** Workspace id */
  workspaceId: string
}
